import React, { useState, useCallback, useEffect } from 'react';
import { knowledgeBaseAPI, documentAPI } from '../services/api';
import { KnowledgeBase } from '@shared/types';
import { useNavigate } from 'react-router-dom';
import './Home.css';

interface KnowledgeBaseStats {
    [key: string]: {
        documentCount: number;
        totalSize: number;
        lastUpload: string;
    };
}


const Home: React.FC = () => {
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
    const [knowledgeBaseStats, setKnowledgeBaseStats] = useState<KnowledgeBaseStats>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const navigate = useNavigate();

    // 加载知识库列表和统计信息
    const loadKnowledgeBases = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            let retryCount = 0;
            const maxRetries = 3;

            while (retryCount < maxRetries) {
                try {
                    // 获取知识库列表
                    const knowledgeBasesResult = await knowledgeBaseAPI.getKnowledgeBases();

                    if (knowledgeBasesResult.success && knowledgeBasesResult.data) {
                        setKnowledgeBases(knowledgeBasesResult.data);

                        // 为每个知识库获取文档列表
                        const stats: KnowledgeBaseStats = {};
                        const documentPromises = knowledgeBasesResult.data.map(async (kb) => {
                            try {
                                const documentsResult = await documentAPI.getKnowledgeBaseDocuments(kb.id);

                                if (documentsResult.success && documentsResult.documents) {
                                    const documents = documentsResult.documents;
                                    const documentCount = documents.length;
                                    const totalSize = documents.reduce((sum, doc) => sum + (doc.file_size || 0), 0);
                                    const lastUpload = documents.length > 0
                                        ? documents.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0].created_at
                                        : '';

                                    stats[kb.id] = {
                                        documentCount,
                                        totalSize,
                                        lastUpload
                                    };
                                } else {
                                    stats[kb.id] = {
                                        documentCount: 0,
                                        totalSize: 0,
                                        lastUpload: ''
                                    };
                                }
                            } catch (error) {
                                console.error(`加载知识库 ${kb.id} 文档列表失败:`, error);
                                stats[kb.id] = {
                                    documentCount: 0,
                                    totalSize: 0,
                                    lastUpload: ''
                                };
                            }
                        });

                        // 等待所有文档列表加载完成
                        await Promise.all(documentPromises);
                        setKnowledgeBaseStats(stats);
                    }

                    break; // 成功则跳出循环
                } catch (error) {
                    retryCount++;
                    console.error(`加载知识库失败 (尝试 ${retryCount}/${maxRetries}):`, error);

                    if (retryCount >= maxRetries) {
                        throw error;
                    }

                    await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
                }
            }
        } catch (error) {
            console.error('加载知识库最终失败:', error);
            setError('加载知识库失败，请检查网络连接或稍后再试');
            setKnowledgeBases([]);
            setKnowledgeBaseStats({});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadKnowledgeBases();
    }, [loadKnowledgeBases]);

    // 进入知识库详情页
    const handleViewKnowledgeBase = (knowledgeBaseId: string) => {
        navigate(`/knowledge-base/${knowledgeBaseId}`);
    };

    // 直接上传到知识库
    const handleUploadToKnowledgeBase = (knowledgeBaseId: string) => {
        navigate(`/upload/${knowledgeBaseId}`);
    };

    // 格式化文件大小
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };



    // 获取知识库图标
    const getKnowledgeBaseIcon = (category: string): string => {
        const iconMap: { [key: string]: string } = {
            '数学': '🔢',
            '计算机科学': '💻',
            '物理': '⚛️',
            '化学': '🧪',
            '生物': '🧬',
            '历史': '📚',
            '地理': '🌍',
            '文学': '📖',
            '艺术': '🎨',
            '语言': '🗣️'
        };
        return iconMap[category] || '📚';
    };

    if (loading) {
        return (
            <div className="home-container">
                <div className="loading-section">
                    <div className="loading-spinner">🔄</div>
                    <p>正在加载知识库...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="home-container">
                <div className="error-section">
                    <div className="error-icon">❌</div>
                    <h2>加载失败</h2>
                    <p>{error}</p>
                    <button onClick={loadKnowledgeBases} className="retry-btn">
                        重试
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="home-container">
            {/* 页面头部 */}
            <div className="home-header">
                <div className="home-header-content">
                    <h1>🏠 AI学习助手</h1>
                    <p className="home-subtitle">管理您的知识库，上传学习文档</p>

                    {/* 总体统计 */}
                    <div className="overall-stats">
                        <div className="stat-card">
                            <div className="stat-icon">📚</div>
                            <div className="stat-number">{knowledgeBases.length}</div>
                            <div className="stat-label">知识库总数</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">📄</div>
                            <div className="stat-number">
                                {Object.values(knowledgeBaseStats).reduce((sum, stats) => sum + (stats.documentCount || 0), 0)}
                            </div>
                            <div className="stat-label">文档总数</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">💾</div>
                            <div className="stat-number">
                                {formatFileSize(Object.values(knowledgeBaseStats).reduce((sum, stats) => sum + (stats.totalSize || 0), 0))}
                            </div>
                            <div className="stat-label">总存储大小</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 主要内容区域 */}
            <div className="main-content">
                {/* 知识库列表 */}
                <div className="knowledge-bases-section">
                    <div className="section-header">
                        <h2>📚 知识库列表</h2>
                        <div className="section-actions">
                            <button
                                onClick={() => navigate('/knowledge-bases')}
                                className="manage-kb-btn"
                            >
                                🗂️ 管理知识库
                            </button>
                            <button
                                onClick={() => navigate('/create-knowledge-base')}
                                className="create-kb-btn"
                            >
                                ➕ 创建知识库
                            </button>
                        </div>
                    </div>

                    {knowledgeBases.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">📭</div>
                            <h3>暂无知识库</h3>
                            <p>创建您的第一个知识库开始学习之旅！</p>
                            <button
                                onClick={() => navigate('/create-knowledge-base')}
                                className="primary-btn"
                            >
                                🚀 创建知识库
                            </button>
                        </div>
                    ) : (
                        <div className="knowledge-bases-list">
                            {knowledgeBases.map((kb) => (
                                <div key={kb.id} className="knowledge-base-item" onClick={() => handleViewKnowledgeBase(kb.id)}>
                                    <div className="kb-item-header">
                                        <div className="kb-item-icon">
                                            {getKnowledgeBaseIcon(kb.category)}
                                        </div>
                                        <div className="kb-item-info">
                                            <h3 className="kb-item-name">{kb.name}</h3>
                                            <p className="kb-item-subject">{kb.subject}</p>
                                            <div className="kb-item-meta">
                                                <span className="kb-item-category">{kb.category}</span>
                                                <span className={`kb-item-status ${kb.status}`}>
                                                    {kb.status === 'active' ? '✅ 活跃' : '⏸️ 暂停'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="kb-item-stats">
                                            <div className="kb-item-stat">
                                                <span>📄</span>
                                                <span className="kb-item-stat-value">
                                                    {knowledgeBaseStats[kb.id]?.documentCount || 0}
                                                </span>
                                            </div>
                                            <div className="kb-item-stat">
                                                <span>📊</span>
                                                <span className="kb-item-stat-value">
                                                    {kb.difficulty_level === 'beginner' ? '初级' :
                                                        kb.difficulty_level === 'intermediate' ? '中级' : '高级'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="kb-item-actions">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewKnowledgeBase(kb.id);
                                            }}
                                            className="kb-item-btn view"
                                            title="查看知识库详情"
                                        >
                                            查看
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleUploadToKnowledgeBase(kb.id);
                                            }}
                                            className="kb-item-btn upload"
                                            title="上传文档到此知识库"
                                        >
                                            上传
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/manage/${kb.id}`);
                                            }}
                                            className="kb-item-btn manage"
                                            title="管理知识库"
                                        >
                                            管理
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* 快速操作 */}
                <div className="quick-actions">
                    <h3>🚀 快速操作</h3>
                    <div className="action-buttons">
                        <button
                            onClick={() => navigate('/upload')}
                            className="quick-action-btn"
                        >
                            📤 上传文档
                        </button>
                        <button
                            onClick={() => navigate('/create-knowledge-base')}
                            className="quick-action-btn"
                        >
                            📚 创建知识库
                        </button>
                        <button
                            onClick={() => navigate('/search')}
                            className="quick-action-btn"
                        >
                            🔍 搜索文档
                        </button>
                        <button
                            onClick={() => navigate('/analytics')}
                            className="quick-action-btn"
                        >
                            📊 学习分析
                        </button>
                        <button
                            onClick={() => navigate('/pdf-demo')}
                            className="quick-action-btn"
                        >
                            📖 PDF查看器演示
                        </button>
                                       <button
                   onClick={() => navigate('/pdf-test')}
                   className="quick-action-btn"
               >
                   🧪 PDF测试页面
               </button>
               <button
                   onClick={() => navigate('/pdf-debug')}
                   className="quick-action-btn"
               >
                   🔧 PDF调试页面
               </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;
